/* eslint-disable sonarjs/no-duplicate-string, no-param-reassign */
import type ChartViewType from 'echarts/types/src/view/Chart';
import ChartView from 'echarts/lib/view/Chart';
import type GlobalModel from 'echarts/types/src/model/Global';
import type SeriesData from 'echarts/types/src/data/SeriesData';
import type ExtensionAPI from 'echarts/types/src/core/ExtensionAPI';
import type { Payload } from 'echarts/types/src/util/types';
import { graphic, getInstanceById } from 'echarts/core';
import View from 'echarts/lib/coord/View';
import type {
  // eslint-disable-next-line import/no-named-default
  default as RoamControllerType,
  RoamControllerHost as RoamControllerHostType
} from 'echarts/types/src/component/helper/RoamController';
import type { TreeNode } from 'echarts/types/src/data/Tree';
import RoamController from 'echarts/lib/component/helper/RoamController';
import { updateViewOnPan, updateViewOnZoom } from 'echarts/lib/component/helper/roamHelper';
import { onIrrelevantElement } from 'echarts/lib/component/helper/cursorHelper';
// eslint-disable-next-line import/no-extraneous-dependencies
import { fromPoints } from 'zrender/lib/core/bbox';
// eslint-disable-next-line import/no-extraneous-dependencies
import type { Group, Element, PathProps } from 'zrender';
import type {
  TwoWayTreeSeriesModelClazz as TwoWayTreeSeriesModel,
  TwoWayTreeSeriesNodeItemOption
} from './TwoWayTreeSeries';
import { deleteUndefinedValueProps } from './util';
import {
  type LayoutInfo,
  NODE_TYPE,
  STATE,
  layoutRootNode,
  layoutNormalNode,
  layoutGroupNode
} from './twoWayTreeLayout';
import { clearSelected, adapt } from './layoutHelper';
import type { CustomEvent } from './types';

function createEl(elOption: { type: string }) {
  const graphicType = elOption.type;
  if (!graphicType) {
    return null;
  }

  let Clz = graphic.getShapeClass(graphicType);
  if (!Clz) {
    //
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    Clz = graphic[graphicType.substring(0, 1).toUpperCase() + graphicType.substring(1)];
  }
  if (!Clz) {
    throw new Error(`graphic type "${graphicType}" can not be found.`);
  }

  return new Clz();
}

function createNode(rootGroup: graphic.Group, node: TreeNode) {
  const nodeData = node.hostTree.data.getRawDataItem(
    node.dataIndex
  ) as TwoWayTreeSeriesNodeItemOption;
  const nodeLayoutInfo = node.getLayout() as LayoutInfo;
  const nodeGroup = new graphic.Group();
  nodeGroup.name = nodeData.name;

  // * node item
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { children: nodeItemChildren = {}, ...nodeItemOption } =
    nodeLayoutInfo?.renderOption?.item || {};
  //
  nodeGroup.attr(nodeItemOption);

  Object.keys(nodeItemChildren).forEach(name => {
    const childOption = nodeItemChildren[name];
    const { type, ...props } = childOption;
    const childEl = createEl(childOption as { type: string });
    if (!childEl) {
      return;
    }

    childEl.attr(props as unknown as PathProps);
    childEl.name = name;

    nodeGroup.add(childEl);
  });

  // * node line
  const { children: nodeLineChildren = {} } = nodeLayoutInfo?.renderOption?.line || {};

  Object.keys(nodeLineChildren).forEach(name => {
    const childOption = nodeLineChildren[name];
    const { type, ...props } = childOption;
    const childEl = createEl(childOption as { type: string });
    if (!childEl) {
      return;
    }

    childEl.attr(props as unknown as PathProps);
    childEl.name = `${nodeGroup.id}@${name}`;

    rootGroup.add(childEl);
  });

  rootGroup.add(nodeGroup);
  return nodeGroup;
}

function nodeToggleSelect(
  data: SeriesData<TwoWayTreeSeriesModel>,
  dataIndex: number,
  rootGroup: graphic.Group,
  seriesModel: TwoWayTreeSeriesModel,
  api: ExtensionAPI
) {
  const node = data.tree.getNodeByDataIndex(dataIndex);
  const nodeData = data.getRawDataItem(dataIndex) as TwoWayTreeSeriesNodeItemOption;
  const isSelected = node.__selected;
  clearSelected(seriesModel, api);
  node.__selected = !isSelected;

  if (!isSelected) {
    const nodeGroup = data.getItemGraphicEl(dataIndex) as Group;
    nodeGroup.childOfName('rect').useState(STATE.SELECTED);

    if (nodeData.type === NODE_TYPE.NORMAL && seriesModel.option.selectedHighlightAncestor) {
      rootGroup.childOfName(`${nodeGroup.id}@line`).useState(STATE.SELECTED_LINK);

      node
        .getAncestors()
        .slice()
        .forEach(ancestorNode => {
          const itemEl = ancestorNode.hostTree.data.getItemGraphicEl(ancestorNode.dataIndex);
          if (!itemEl) {
            return;
          }

          itemEl.traverse(
            childEl =>
              childEl?.ensureState(STATE.SELECTED_LINK) && childEl?.useState(STATE.SELECTED_LINK)
          );

          rootGroup.childOfName(`${itemEl.id}@line`)?.useState(STATE.SELECTED_LINK);
        });
    }
  }
}

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
function updateNode(
  data: SeriesData<TwoWayTreeSeriesModel>,
  dataIndex: number,
  el: Element,
  rootGroup: graphic.Group,
  seriesModel: TwoWayTreeSeriesModel,
  api: ExtensionAPI
) {
  const layout = data.getItemLayout(dataIndex) as LayoutInfo;

  if (!layout || Number.isNaN(layout.x) || Number.isNaN(layout.y) || layout.noRender) {
    return;
  }

  const node = data.tree.getNodeByDataIndex(dataIndex);
  const nodeData = data.getRawDataItem(dataIndex) as TwoWayTreeSeriesNodeItemOption;

  if (nodeData.type === NODE_TYPE.ROOT) {
    layoutRootNode(node, seriesModel);
  } else if (nodeData.type === NODE_TYPE.NORMAL) {
    layoutNormalNode(node, seriesModel);
  } else if (nodeData.type === NODE_TYPE.GROUP) {
    layoutGroupNode(node, seriesModel);
  }
  const nodeGroup = createNode(rootGroup, node);
  data.setItemGraphicEl(dataIndex, nodeGroup as unknown as Group);

  // * EVENT
  if (nodeData.type === NODE_TYPE.ROOT || nodeData.type === NODE_TYPE.NORMAL) {
    if (node.__selected) {
      node.__selected = false;
      nodeToggleSelect(data, dataIndex, rootGroup, seriesModel, api);
    }

    nodeGroup.off('click').on('click', () => {
      nodeToggleSelect(data, dataIndex, rootGroup, seriesModel, api);

      const isSelected = node.__selected as boolean;
      if (isSelected) {
        let viewCenter = seriesModel.get('viewCenterAfterClick');
        if (typeof viewCenter === 'function') {
          viewCenter = viewCenter({ node, data: nodeData, isSelected }) as [number, number];
        }

        adapt(seriesModel, api, {
          animation: true,
          target: nodeGroup,
          noScale: true,
          viewCenter
        });
      }

      getInstanceById(api.getId()).trigger('dvclick.twowaytreenodeitem', {
        type: 'dvclick.twowaytreenodeitem',
        node,
        data: nodeData,
        isExpand: node.isExpand,
        isSelected,
        selected: isSelected
      } as CustomEvent);

      //
    });
  }
  if (nodeData.type === NODE_TYPE.GROUP) {
    nodeGroup
      // .childOfName('circle')
      .off('click')
      .on('click', () => {
        api.dispatchAction({
          type: 'dvTwoWayTreeExpandAndCollapse',
          tree: seriesModel.leftTree.data === data ? 'left' : ' right',
          dataIndex
        });

        getInstanceById(api.getId()).trigger('dvclick.twowaytreenodeitem', {
          type: 'dvclick.twowaytreenodeitem',
          node,
          data: nodeData,
          isExpand: node.isExpand
        } as CustomEvent);
      });
  }

  //
}

// eslint-disable-next-line no-use-before-define
export type TwoWayTreeViewClazz = TwoWayTreeView & ChartViewType;

export default class TwoWayTreeView extends ChartView {
  static readonly type = 'dvTwoWayTree';

  readonly type = TwoWayTreeView.type;

  private __controller: RoamControllerType;

  controllerHost: RoamControllerHostType;

  private __min: number[];

  private __max: number[];

  private __leftTreeData: SeriesData;

  private __rightTreeData: SeriesData;

  private __originScale: number;

  /* eslint-disable */
  init(this: TwoWayTreeViewClazz, ecModel: GlobalModel, api: ExtensionAPI) {
    this.__controller = new RoamController(api.getZr());

    this.controllerHost = {
      target: this.group
    } as RoamControllerHostType;
  }
  /* eslint-enable */

  render(
    this: TwoWayTreeViewClazz,
    seriesModel: TwoWayTreeSeriesModel,
    ecModel: GlobalModel,
    api: ExtensionAPI,
    payload: Payload
  ) {
    // eslint-disable-next-line no-param-reassign
    api.getDom().style.backgroundColor = seriesModel.get('backgroundColor');

    // see src/chart/tree/TreeView.ts
    const group = this.group as unknown as Group;
    group.removeAll();

    const leftTreeData = seriesModel.leftTree.data;
    const oldLeftTreeData = this.__leftTreeData;

    leftTreeData
      .diff(null /* oldLeftTreeData */)
      .add(newIdx => {
        updateNode(leftTreeData, newIdx, null, group, seriesModel, api);
      })
      .update(function (newIdx, oldIdx) {
        //
      })
      .remove(function (oldIdx) {
        //
      })
      .execute();

    this.__leftTreeData = leftTreeData;

    const rightTreeData = seriesModel.rightTree.data;
    const oldRightTreeData = this.__rightTreeData;

    rightTreeData
      .diff(null /* oldRightTreeData */)
      .add(function (newIdx) {
        updateNode(rightTreeData, newIdx, null, group, seriesModel, api);
      })
      .update(function (newIdx, oldIdx) {
        //
      })
      .remove(function (oldIdx) {
        //
      })
      .execute();

    this.__rightTreeData = rightTreeData;

    if (payload) {
      group.decomposeTransform();

      if (
        payload?.type === 'dvTwoWayTreeExpandToInitDepth' ||
        payload?.type === 'dvTwoWayTreeExpandAllNode'
      ) {
        adapt(seriesModel, api, {
          animation: {
            done: () => {
              this.controllerHost.zoom = this.group.scaleX / this.__originScale;
            }
          }
        });
      }
    } else {
      this.updateViewCoordSys(seriesModel, api);
      this.updateController(seriesModel, ecModel, api);

      adapt(seriesModel, api);
      this.__originScale = this.group.scaleX;
    }
  }

  /* eslint-disable */
  updateViewCoordSys(
    this: TwoWayTreeViewClazz,
    seriesModel: TwoWayTreeSeriesModel,
    api: ExtensionAPI
  ) {
    const points: number[][] = [];
    const leftTreeData = seriesModel.leftTree.data;
    leftTreeData.each(function (idx) {
      const layout = leftTreeData.getItemLayout(idx);
      if (layout && !isNaN(layout.x) && !isNaN(layout.y)) {
        points.push([+layout.x, +layout.y]);
      }
    });
    const rightTreeData = seriesModel.rightTree.data;
    rightTreeData.each(function (idx) {
      const layout = rightTreeData.getItemLayout(idx);
      if (layout && !isNaN(layout.x) && !isNaN(layout.y)) {
        points.push([+layout.x, +layout.y]);
      }
    });

    const min: number[] = [];
    const max: number[] = [];
    fromPoints(points, min, max);

    // If don't Store min max when collapse the root node after roam,
    // the root node will disappear.
    const oldMin = this.__min;
    const oldMax = this.__max;

    // If width or height is 0
    if (max[0] - min[0] === 0) {
      min[0] = oldMin ? oldMin[0] : min[0] - 1;
      max[0] = oldMax ? oldMax[0] : max[0] + 1;
    }
    if (max[1] - min[1] === 0) {
      min[1] = oldMin ? oldMin[1] : min[1] - 1;
      max[1] = oldMax ? oldMax[1] : max[1] + 1;
    }

    const viewCoordSys = (seriesModel.coordinateSystem = new View());
    viewCoordSys.zoomLimit = seriesModel.get('scaleLimit');

    viewCoordSys.setBoundingRect(min[0], min[1], max[0] - min[0], max[1] - min[1]);

    viewCoordSys.setCenter(seriesModel.get('center'), api);
    viewCoordSys.setZoom(seriesModel.get('zoom'));

    // Here we use viewCoordSys just for computing the 'position' and 'scale' of the group
    this.group.attr({
      x: viewCoordSys.x,
      y: viewCoordSys.y,
      scaleX: viewCoordSys.scaleX,
      scaleY: viewCoordSys.scaleY
    });

    this.__min = min;
    this.__max = max;
  }

  updateController(
    this: TwoWayTreeViewClazz,
    seriesModel: TwoWayTreeSeriesModel,
    ecModel: GlobalModel,
    api: ExtensionAPI
  ) {
    const controller = this.__controller;
    const controllerHost = this.controllerHost;
    const { group } = this;

    controller.setPointerChecker(function (e, x, y) {
      // ! 让整个画布都可以生效
      return true;
    });

    controller.enable(seriesModel.get('roam'));
    controllerHost.zoomLimit = seriesModel.get('scaleLimit');
    //
    controllerHost.zoom = seriesModel.coordinateSystem.getZoom();

    controller
      .off('pan')
      .off('zoom')
      .on('pan', e => {
        updateViewOnPan(controllerHost, e.dx, e.dy);
      })
      .on('zoom', e => {
        updateViewOnZoom(controllerHost, e.scale, e.originX, e.originY);
      });
  }
  /* eslint-enable */

  _transformTo(
    this: TwoWayTreeViewClazz,
    option: {
      originX?: number;
      originY?: number;
      scaleX?: number;
      scaleY?: number;
      rotation?: number;
      x?: number;
      y?: number;
    },
    animation:
      | boolean
      | { time?: number; during?: (percent: number) => void; done?: () => void } = false
  ) {
    let props = {
      ...option
    };
    props = deleteUndefinedValueProps(props);

    if (animation === false) {
      this.group.attr(props);

      return this;
    }

    let animationOption: { time?: number; during?: (percent: number) => void; done?: () => void } =
      { time: 2e2 };

    if (animation !== true) {
      animationOption = { ...animationOption, ...animation };
    }

    this.group
      .animate('', false)
      .when(animationOption.time, props)
      .during((_, percent: number) => {
        if (typeof animationOption.during === 'function') {
          animationOption.during(percent);
        }
      })
      .done(() => {
        if (typeof animationOption.done === 'function') {
          animationOption.done();
        }
      })
      .start();

    return this;
  }

  dispose(this: TwoWayTreeViewClazz) {
    this?.__controller?.dispose();
    this.controllerHost = null;
  }

  remove(this: TwoWayTreeViewClazz) {
    this.group.removeAll();
    this.__leftTreeData = null;
    this.__rightTreeData = null;
  }

  //
}
